// AboutUs.js
import React, { useEffect, useState } from "react";
import "./AboutUs.css";
import { Helmet, HelmetProvider } from "react-helmet-async";

const AboutUs = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  const seoSteps = [
    {
      title: "Keyword Research",
      description: "Identifying the right keywords for your target audience",
      icon: "🔍",
    },
    {
      title: "Content Optimization",
      description: "Creating high-quality, relevant content for your keywords",
      icon: "✍️",
    },
    {
      title: "Technical SEO",
      description: "Improving site speed, mobile-friendliness, and structure",
      icon: "⚙️",
    },
    {
      title: "Backlink Building",
      description: "Earning links from authoritative websites",
      icon: "🔗",
    },
    {
      title: "Ranking & Traffic",
      description: "Achieving higher search rankings and more visitors",
      icon: "📈",
    },
  ];

  useEffect(() => {
    // Animation trigger when component mounts
    setIsVisible(true);

    // Auto-rotate through SEO steps
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % seoSteps.length);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="about-us-container">
      <Helmet>
        <title>Best SEO & Digital Marketing Company in India | Just SEO</title>
        <meta
          name="description"
          content="Just SEO offers top-rated SEO and digital marketing services in India to grow your business online. Rank higher, drive traffic, and convert leads with our proven strategies."
        />
        <meta
          name="keywords"
          content="AI SEO, artificial intelligence SEO, machine learning SEO, ChatGPT for SEO, AI content optimization, Google Bard SEO, NLP for SEO, AI keyword research, automated SEO, predictive SEO, AI backlink analysis, SEO automation tools, AI-powered rankings, generative AI SEO, AI meta descriptions, SEO machine learning,AI SEO tools, ChatGPT SEO prompts, Google Gemini SEO, multilingual AI SEO, AI content detection, AI for technical SEO, AI-powered backlinks, AI SERP analysis, AI for featured snippets, AI SEO audits, AI for local SEO, AI eCommerce SEO, AI video SEO, AI for voice search, AI content scoring, SEO machine learning models, neural matching optimization, AI for zero-click SEO, SEO services, digital marketing agency, search engine optimization, on-page SEO, off-page SEO, local SEO, eCommerce SEO, Google ranking, SEO expert, PPC advertising, social media marketing, content marketing, email marketing, YouTube SEO, voice search SEO, AI SEO, backlink building, keyword research, technical SEO, mobile SEO, Shopify SEO, WordPress SEO, SEO audit, Google Ads, Facebook Ads, LinkedIn marketing, SEO consultant, affordable SEO services, SEO for small business,content marketing, email marketing, influencer marketing, affiliate marketing, Google Ads, Facebook Ads, best SEO strategies 2024, how to improve Google ranking, SEO for small businesses, e-commerce SEO tips, voice search optimization, AI in digital marketing, Meta ads management, YouTube SEO techniques, mobile SEO best practices, SEO vs SEM,Search engine optimization,SEO services,SEO agency,SEO consultant,SEO expert,SEO company,SEO audit,SEO tools,Google ranking,Organic search traffic,Backlink building,Broken link building,Digital PR for SEO,Page speed optimization,SEO near me,Voice search optimization,Mobile-first indexing,Global SEO strategy,Multiregional SEO,How to improve SEO ranking, Real estate lead generation, Healthcare marketing,How to get backlinks naturally,Google Search Console,Google Analytics 4 (GA4),Rank tracking,Click-through rate (CTR),Bounce rate SEO,Dwell time,Organic impressions,Search query reports,Cannibalization tracking,SEO KPI dashboard,AI tools for digital marketing,AI tools for digital marketing"
        />
        <meta property="og:title" content="About Just SEO | Our Story & Services" />
        <meta property="og:url" content="https://justseo.in/about-us" />
        <meta
          property="og:image"
          content="https://justseo.in/faviconcopy.png"
        />
        <meta name="robots" content="index, follow" />

      </Helmet>
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <h1>About Just SEO</h1>
          <p className="hero-subtitle">
            Where Digital Excellence Meets Results
          </p>
        </div>
      </section>

      <div className={`seo-animation-container ${isVisible ? "visible" : ""}`}>
        {/* <h2 className="seo-title">How SEO Works</h2> */}
        <div className="seo-steps-container">
          {seoSteps.map((step, index) => (
            <div
              key={index}
              className={`seo-step ${index === activeIndex ? "active" : ""}`}
              style={{ "--delay": index * 0.1 + "s" }}
            >
              <div className="step-icon">{step.icon}</div>
              <h3 className="step-title">{step.title}</h3>
              <p className="step-description">{step.description}</p>
            </div>
          ))}
        </div>
        <div className="seo-visualization">
          {seoSteps.map((_, index) => (
            <div
              key={index}
              className={`visual-step ${index === activeIndex ? "active" : ""}`}
              onClick={() => setActiveIndex(index)}
            />
          ))}
        </div>
      </div>

      {/* Main About Section */}
      <section className="about-section">
        <div className="section-container">
          <h2 className="section-title">Our Story</h2>
          <div className="section-divider"></div>
          <p className="about-text">
            Just SEO is a global leader in next-generation digital marketing
            services, solutions, and consulting. Our digitally-savvy marketing
            specialists understand the connection between content and customers.
            Our reputation is built on a decade of successfully delivering
            measurable results. Spectacular journey of over a decade with
            thousands of delighted customers, 24hrs excellent support with the
            world's biggest in-house digital marketing squad.
          </p>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="section-container">
          <h2 className="section-title">By The Numbers</h2>
          <div className="section-divider"></div>
          <div className="stats-grid">
            {[
              { value: "11+", label: "Years of Experience" },
              { value: "5+", label: "Countries we are Present In" },
              { value: "97%", label: "Repeat Customers" },
              { value: "45+", label: "In House Team" },
              { value: "1.5M", label: "Hours of Experience" },
              { value: "18,500", label: "Projects Delivered" },
            ].map((stat, index) => (
              <div key={index} className="stat-item">
                <h3 className="stat-value-about">{stat.value}</h3>
                <p className="stat-label">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="vision-mission-section">
        <div className="section-container vm-container">
          <div className="vision">
            <h2 className="section-title">Our Vision</h2>
            <div className="section-divider"></div>
            <p>
              Our vision is to blend Digital Marketing with Artificial
              Intelligence & Data Science to help our clients crush the
              competition and stay ahead.
            </p>
          </div>
          <div className="mission">
            <h2 className="section-title">Our Mission</h2>
            <div className="section-divider"></div>
            <p>
              We aim to provide 100% value to our clients and maximize their
              business ROI with our Modern Digital Marketing strategies.
            </p>
          </div>
        </div>
      </section>

      {/* Achievements */}
      <section className="achievements-section">
        <div className="section-container">
          <h2 className="section-title">Our Achievements</h2>
          <div className="section-divider"></div>
          <div className="achievements-grid">
            {[
              {
                value: "1,42,832",
                title: "KEYWORD RANKED",
                description: "For our clients with strategic SEO",
              },
              {
                value: "1M+",
                title: "LEAD GENERATED",
                description: "Through Data Driven Digital Marketing approach",
              },
              {
                value: "1000+",
                title: "CASE STUDIES",
                description: "See how we delighted our clients",
              },
              {
                value: "24x5",
                title: "Operational",
                description: "Sun never sets in Just SEO",
              },
            ].map((achievement, index) => (
              <div key={index} className="achievement-item">
                <h3 className="achievement-value">{achievement.value}</h3>
                <p className="achievement-title">{achievement.title}</p>
                <p className="achievement-description">
                  {achievement.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="values-section">
        <div className="section-container">
          <h2 className="section-title">Our Core Values</h2>
          <div className="section-divider"></div>
          <p className="values-text">
            We establish trust, show care and respect, communicate humbly,
            listen deeply, and express gratitude to our clients and colleagues.
          </p>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
